import React, { useState, useEffect, useRef, useMemo } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { LoadingIndicator } from 'lib/components';
import RouteLeavingGuard from 'app/pages/video/videoDetails/main/RouteLeavingGuard';
import { Button } from 'react-covideo-common';
import { FullScreenModal } from 'lib/components/FullScreenModal';
import {
  ParagraphNormalBold600,
  SubHeading,
} from 'lib/components/styles/typography';
import { Flex } from 'lib/components/styles/layout';
import { Divider } from 'app/pages/admin/index.styled';
import { MdClose } from 'react-icons/md';
import { useGetFolderAccessSettingsOptions } from 'lib/api/folderAccess/useGetFolderAccessSettingsOptions';
import { useAddFolderAccessSettings } from 'lib/api/folderAccess/useAddFolderAccessSettings';
import { useGetFolderAccessSettings } from 'lib/api/folderAccess/useGetFolderAccessSettings';
import { MessageAndNotification } from './MessageAndNotification';
import { ManageAccessSection } from './ManageAccessSection';
import {
  FolderAccessSettings,
  LabelOption,
  MatchParams,
  Option,
} from './types';
import {
  AccessOptions,
  AutomotiveRoleOptions,
  OemOptions,
} from 'lib/api/folderAccess/types';
import { useTheme } from 'styled-components/macro';
import { Formik } from 'formik';
import { ShareWithAndFolderAccess } from './ShareWithAndFolderAccess';
import { SelectOem } from './SelectOem';
import { LabelText } from 'lib/components/folders/style';
import { RegionsAndStores } from './RegionsAndStores';
import { useGetFoldersV2 } from 'lib/api/folders/getFoldersV2';
import { sanitizeText } from 'lib/utils/functions';
import { NotFound } from 'app/pages/notFound/NotFound';
import { useTranslation } from 'react-i18next';

export const FolderShareOemModal = ({
  match,
}: RouteComponentProps<MatchParams>) => {
  const { folderId, page } = match.params;
  const history = useHistory();
  const { t } = useTranslation();

  const [showAccessSection, setShowAccessSection] = useState(false);
  const [accessLevelOptions, setAccessLevelOptions] = useState<AccessOptions[]>(
    []
  );
  const [automotiveRoleOptions, setAutomotiveRoleOptions] = useState<
    AutomotiveRoleOptions[]
  >([]);
  const [oemDropdownOptions, setOemDropdownOptions] = useState<OemOptions[]>(
    []
  );
  const [selectedOem, setSelectedOem] = useState({
    id: 0,
    name: '',
    regions: [],
    stores: [],
    value: '0',
    label: (<>OEM</>) as React.JSX.Element,
  } as OemOptions);
  const [selectedRegions, setSelectedRegions] = useState(
    [] as AutomotiveRoleOptions[]
  );
  const [selectedStores, setSelectedStores] = useState(
    [] as AutomotiveRoleOptions[]
  );
  const [selectedAutomotiveRoles, setSelectedAutomotiveRoles] = useState(
    [] as AutomotiveRoleOptions[]
  );
  const [selectedAccessLevel, setSelectedAccessLevel] = useState({
    value: '0',
    label: (<></>) as React.JSX.Element,
  } as LabelOption);
  const [accessList, setAccessList] = useState([] as FolderAccessSettings[]);
  const [expandAccessSection, setExpandAccessSection] = useState(false);
  const [initialAccessList, setInitialAccessList] = useState(
    [] as FolderAccessSettings[]
  );
  const [oemRegionOptions, setOemRegionOptions] = useState<Option[]>([]);
  const [oemStoreOptions, setOemStoreOptions] = useState<Option[]>([]);
  const [isRegionsDisabled, setIsRegionsDisabled] = useState(false);
  const [isStoresDisabled, setIsStoresDisabled] = useState(false);

  const themes = useTheme();
  const skipGuardRef = useRef(false);
  const formikHelpersRef = useRef<{ setFieldValue: any } | null>(null);
  const { mutateAsync: saveAccessSettings } = useAddFolderAccessSettings();

  const { refetch: refetchFolders } = useGetFoldersV2({
    params: { getShared: true },
  });
  const {
    data: oemFolderOptions,
    isLoading,
    refetch: refetchOptions,
    error: accessOptionsError,
  } = useGetFolderAccessSettingsOptions(Number(folderId), true);

  const {
    data: existingAccess,
    refetch: refetchAccess,
    error: folderAccessError,
  } = useGetFolderAccessSettings(Number(folderId));

  const formikInitialValues = useMemo(
    () => ({
      message: '',
      notify: false,
      accessList: initialAccessList,
    }),
    [initialAccessList]
  );

  const showLoader = isLoading || !oemFolderOptions;

  useEffect(() => {
    refetchOptions();
    refetchAccess();
  }, [folderId]);

  useEffect(() => {
    if (!oemFolderOptions) {
      return;
    }
    const accessOptions = oemFolderOptions.accessOptions.map(option => ({
      ...option,
      name: t(option.name),
    }));
    setAccessLevelOptions(accessOptions);
    const automotiveRoleOptions = (
      oemFolderOptions?.oemShareWithOptions || []
    ).map((item: AutomotiveRoleOptions) => {
      return {
        ...item,
        value: String(item.id),
        label: t(sanitizeText(item.name)),
        name: t(sanitizeText(item.name)),
      };
    });
    if (
      !oemFolderOptions.oemOptions ||
      oemFolderOptions.oemOptions.length < 1
    ) {
      return;
    }
    const oemDropdownOptions = oemFolderOptions.oemOptions.map(
      (item: OemOptions) => {
        return {
          ...item,
          value: String(item.id),
          label: sanitizeText(item.name),
        };
      }
    );
    setOemDropdownOptions(oemDropdownOptions);
    setAutomotiveRoleOptions(automotiveRoleOptions);
  }, [oemFolderOptions]);

  useEffect(() => {
    const selectedOemDetails = (oemFolderOptions?.oemOptions || []).find(
      (item: OemOptions) => item.id === Number(selectedOem.value)
    );

    if (!selectedOemDetails) {
      setOemRegionOptions([]);
      setOemStoreOptions([]);
      return;
    }
    const regions = selectedOemDetails?.regions || [];
    const stores = selectedOemDetails?.stores || [];
    const regionDropdownOptions = regions.map((item: OemOptions) => {
      return {
        ...item,
        value: String(item.id),
        label: sanitizeText(item.name),
      };
    });
    const storeDropdownOptions = stores.map((item: OemOptions) => {
      return {
        ...item,
        value: String(item.id),
        label: sanitizeText(item.name),
      };
    });
    setOemRegionOptions(regionDropdownOptions);
    setOemStoreOptions(storeDropdownOptions);
  }, [selectedOem]);

  useEffect(() => {
    if (!existingAccess?.length) {
      setInitialAccessList([]);
      setAccessList([]);
      return;
    }

    const translatedList = existingAccess.map(item => ({
      ...item,
      targetAutomotiveRoleName: t(item.targetAutomotiveRoleName || ''),
    }));
    if (existingAccess[0].targetRegionId) {
      setIsStoresDisabled(true);
    } else if (existingAccess[0].targetCustomerId) {
      setIsRegionsDisabled(true);
    }
    setInitialAccessList(translatedList);
    setAccessList(translatedList);
    setShowAccessSection(true);
    setExpandAccessSection(true);
  }, [existingAccess]);

  useEffect(() => {
    const hasAccess = accessList.length > 0;
    setShowAccessSection(hasAccess);
    setExpandAccessSection(hasAccess);
    if (formikHelpersRef.current) {
      formikHelpersRef.current.setFieldValue('accessList', accessList, true); // "true" triggers validation and dirty check
    }
  }, [accessList]);

  useEffect(() => {
    if (
      !oemFolderOptions ||
      !oemFolderOptions.oemOptions ||
      oemFolderOptions.oemOptions.length != 1
    ) {
      return;
    }
    setSelectedOem({
      ...oemFolderOptions.oemOptions[0],
      label: (
        <LabelText>
          {sanitizeText(oemFolderOptions.oemOptions[0].name)}
        </LabelText>
      ),
      value: String(oemFolderOptions.oemOptions[0].id),
    });
  }, [selectedAutomotiveRoles]);

  const goToHome = () => {
    history.push(`/home?folderId=${folderId}&page=${page}`);
  };

  const handleSaveAndShare = async (values: any) => {
    skipGuardRef.current = true;

    const dataToSend = {
      folderId: Number(folderId),
      sendNotification: !!values.notify,
      message: values.message,
      forOem: true,
      accessSettings: values.accessList,
    };
    await saveAccessSettings(dataToSend);
    await refetchFolders();
    goToHome();
  };

  const assignAccessToSelected = () => {
    const isStoreSelected = selectedRegions?.length === 0;
    const fallbackTargets = isStoreSelected ? selectedStores : selectedRegions;

    const result = selectedAutomotiveRoles.flatMap(role =>
      fallbackTargets.map(target => ({
        targetAutomotiveRoleId: Number(role.id),
        targetAutomotiveRoleName: sanitizeText(role.name),
        targetOrganizationId: Number(selectedOem.id),
        targetOrganizationName: sanitizeText(selectedOem.name),
        ...(isStoreSelected
          ? {
              targetCustomerId: Number(target.id),
              targetCustomerName: sanitizeText(target.name),
            }
          : {
              targetRegionId: Number(target.id),
              targetRegionName: sanitizeText(target.name),
            }),
        isDeleted: false,
      }))
    );

    const temporaryAccessList = [...accessList];
    for (const opt of result) {
      const checkExistingIndex = accessList.findIndex(
        (entry: FolderAccessSettings) => {
          const isSameOrg =
            entry.targetOrganizationId === opt.targetOrganizationId;
          const isSameRole =
            entry.targetAutomotiveRoleId === opt.targetAutomotiveRoleId;

          const isSameLocation =
            'targetCustomerId' in opt
              ? 'targetCustomerId' in entry &&
                entry.targetCustomerId === opt.targetCustomerId
              : 'targetRegionId' in entry &&
                entry.targetRegionId === opt.targetRegionId;

          return isSameOrg && isSameRole && isSameLocation;
        }
      );

      if (checkExistingIndex >= 0) {
        temporaryAccessList[checkExistingIndex] = {
          ...temporaryAccessList[checkExistingIndex],
          access: selectedAccessLevel.value,
          isDeleted: false,
        };
      } else {
        temporaryAccessList.push({
          ...opt,
          access: selectedAccessLevel.value,
          folderId: Number(folderId),
        });
      }
    }
    if (temporaryAccessList[0].targetRegionId) {
      setIsStoresDisabled(true);
    } else if (temporaryAccessList[0].targetCustomerId) {
      setIsRegionsDisabled(true);
    }
    setAccessList(temporaryAccessList);
    setSelectedOem({
      id: 0,
      name: '',
      regions: [],
      stores: [],
      value: '0',
      label: <LabelText>{sanitizeText('OEM')}</LabelText>,
    } as OemOptions);
    setSelectedRegions([]);
    setSelectedStores([]);
    setSelectedAutomotiveRoles([]);
  };

  if (Number(folderId) <= 0 || accessOptionsError || folderAccessError) {
    return <NotFound />;
  }

  return (
    <>
      {showLoader && <LoadingIndicator isLoading={isLoading as boolean} />}
      {!showLoader && (
        <>
          <Formik
            initialValues={formikInitialValues}
            onSubmit={values => handleSaveAndShare(values)}
            validateOnMount
            enableReinitialize
          >
            {({ isSubmitting, submitForm, dirty, values, setFieldValue }) => {
              formikHelpersRef.current = { setFieldValue };
              return (
                <>
                  <RouteLeavingGuard
                    when={true}
                    stay={true}
                    onConfirm={() => {}}
                    onDiscard={() => goToHome()}
                    navigate={_ => goToHome()}
                    shouldBlockNavigation={() => {
                      return !skipGuardRef.current && dirty;
                    }}
                    title={t('Discard changes?')}
                    text={t(
                      'You have unsaved changes. Leaving now will discard them. Do you want to continue?'
                    )}
                    confirmButtonText={t('Continue Editing')}
                    discardButtonText={t('Discard Changes')}
                    showDiscardIcon={false}
                    showSaveIcon={false}
                    fromSharedFolder={true}
                  />
                  <FullScreenModal
                    headerLeft={
                      <Flex alignItems='center'>
                        <Button
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: themes.colors.black[100],
                          }}
                          icon={<MdClose size='18' />}
                          onClick={() => goToHome()}
                        />
                      </Flex>
                    }
                    headerCenter={
                      <ParagraphNormalBold600>
                        {t('Share Folder')}
                      </ParagraphNormalBold600>
                    }
                    headerRight={
                      <Flex justifyContent='flex-end' gap='8px'>
                        <Button
                          text={t('Save & Share')}
                          disabled={isSubmitting || !dirty}
                          onClick={() => submitForm()}
                        />
                      </Flex>
                    }
                  >
                    <Flex width='798px' maxWidth='100%' margin='0 auto'>
                      <Flex
                        justifyContent='center'
                        alignItems='flex-start'
                        gap='20px'
                      >
                        <SubHeading>{t('Manage Access')}</SubHeading>
                        <Flex gap='20px'>
                          <ShareWithAndFolderAccess
                            setSelectedValues={setSelectedAutomotiveRoles}
                            selectedValues={selectedAutomotiveRoles}
                            accessOptionsData={accessLevelOptions}
                            shareWithOptionsData={automotiveRoleOptions}
                            setSelectedAccess={setSelectedAccessLevel}
                            selectedAccess={selectedAccessLevel}
                          />
                          {oemDropdownOptions.length > 1 && (
                            <SelectOem
                              oemDropdownOptions={oemDropdownOptions}
                              setSelectedOem={setSelectedOem}
                              selectedOem={selectedOem}
                              selectedEntities={selectedAutomotiveRoles}
                            />
                          )}
                          <RegionsAndStores
                            regionDropdownOptions={oemRegionOptions}
                            storeDropdownOptions={oemStoreOptions}
                            setSelectedRegions={setSelectedRegions}
                            setSelectedStores={setSelectedStores}
                            selectedRegions={selectedRegions}
                            selectedStores={selectedStores}
                            selectedOem={selectedOem}
                            selectedAutomotiveRoles={selectedAutomotiveRoles}
                            isRegionsDisabled={isRegionsDisabled}
                            isStoresDisabled={isStoresDisabled}
                          />
                          <Divider
                            maxWidth='798px'
                            marginTop='4px'
                            marginBottom='4px'
                          />
                          <Button
                            style={{ width: '180px', alignSelf: 'flex-start' }}
                            onClick={() => assignAccessToSelected()}
                            text={t('Assign Access')}
                            variant='secondary'
                            disabled={
                              !selectedStores?.length &&
                              !selectedRegions?.length
                            }
                          />
                          <MessageAndNotification
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        </Flex>
                        {!!showAccessSection && (
                          <ManageAccessSection
                            forOem={true}
                            accessOptions={accessLevelOptions}
                            accessList={accessList}
                            expandAccessSection={expandAccessSection}
                            setAccessList={setAccessList}
                            setExpandAccessSection={setExpandAccessSection}
                          />
                        )}
                      </Flex>
                    </Flex>
                  </FullScreenModal>
                </>
              );
            }}
          </Formik>
        </>
      )}
    </>
  );
};
